import { useEffect, useState, type FormEvent } from "react";
import { Api, type Me, type ShareRole, type TransferOffer } from "../api.js";
import { useI18n } from "../i18n.js";
import { TextInput } from "../components/ui.js";
import { AuthShell, errorText } from "./Screens.js";

/** `#/accept-share?token=...`: opened from the e-mail. Mirrors the account invitation screen. */
export function AcceptShareScreen({ api, token, me, onJoined }: { api: Api; token: string; me: Me | null; onJoined: (agentId: string, me: Me | null) => void }) {
  const { t, lang } = useI18n();
  const [info, setInfo] = useState<{ agentName: string; accountName: string; email: string; role: "viewer" | "editor" | "manager"; hasLogin: boolean } | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.lookupShare(token).then(setInfo, () => setInvalid(true));
  }, [api, token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.acceptShare({ token, name: name.trim() || undefined, password: password || undefined, language: lang });
      onJoined(r.sharedAgentId, r.user ? (r as Me) : null);
    } catch (err) {
      setError(errorText(err, t("common.network")));
    } finally {
      setBusy(false);
    }
  };

  if (invalid) return <AuthShell title={t("shareAccept.title")}><div className="banner warn" role="alert">{t("shareAccept.invalid")}</div></AuthShell>;
  if (!info) return <AuthShell title={t("shareAccept.title")}><p>{t("common.loading")}</p></AuthShell>;
  const signedInAsInvited = me?.user.email.toLowerCase() === info.email.toLowerCase();
  return (
    <AuthShell title={t("shareAccept.title")} lead={t("shareAccept.body", { agent: info.agentName, account: info.accountName, role: t(`role.${info.role}`).toLowerCase() })}>
      {info.hasLogin && !signedInAsInvited && (
        <>
          <p>{t("shareAccept.signInFirst", { email: info.email })}</p>
          <a className="btn primary wide" href="#/login">
            {t("login.submit")}
          </a>
        </>
      )}
      {info.hasLogin && signedInAsInvited && (
        <>
          {error && <div className="banner warn" role="alert">{error}</div>}
          <button className="btn primary wide" disabled={busy} onClick={(e) => void submit(e)}>
            {t("shareAccept.accept")}
          </button>
        </>
      )}
      {!info.hasLogin && (
        <form onSubmit={submit} noValidate>
          <p>{t("shareAccept.newPerson", { email: info.email })}</p>
          <TextInput label={t("common.name")} autoComplete="name" value={name} onChange={setName} required />
          <TextInput label={t("common.password")} type="password" autoComplete="new-password" value={password} onChange={setPassword} required help={t("signup.passwordHelp")} error={error ?? undefined} />
          <button className="btn primary wide" type="submit" disabled={busy || !name.trim() || !password}>
            {t("shareAccept.accept")}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

/** An ownership offer, by e-mailed token or by id from the agent list. The person must be signed in as the invited address. */
export function AcceptTransferScreen({ api, me, token, id, onDone }: { api: Api; me: Me; token?: string; id?: string; onDone: (me: Me, agentId: string) => void }) {
  const { t } = useI18n();
  const ref = token ? { token } : { id };
  const [offer, setOffer] = useState<TransferOffer | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [account, setAccount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [people, setPeople] = useState<{ userId: string; name: string; email: string; role: ShareRole }[] | null>(null);
  const [agentId, setAgentId] = useState("");
  const [overPlan, setOverPlan] = useState<{ message: string; upgradeUrl?: string } | null>(null);

  useEffect(() => {
    api.lookupTransfer(ref).then(
      (o) => {
        setOffer(o);
        setAccount(o.accounts?.[0]?.id ?? "");
      },
      () => setInvalid(true),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, token, id]);

  if (outcome) {
    const remove = (userId: string) => api.removeShare(agentId, userId).then(() => setPeople((l) => (l ?? []).filter((p) => p.userId !== userId)), (err) => setError(errorText(err, t("common.network"))));
    return (
      <main className="page" id="main">
        <div className="banner ok" role="status">{outcome}</div>
        {overPlan && (
          <div className="banner warn" role="status">
            <p>{t("transfer.overPlan")}</p>
            <p className="hint">{overPlan.message}</p>
            {overPlan.upgradeUrl && (
              <a className="btn primary" href={overPlan.upgradeUrl}>
                {t("plan.upgrade")}
              </a>
            )}
          </div>
        )}
        {people && (
          <>
            <h2>{t("transfer.peopleTitle")}</h2>
            {error && <div className="banner warn" role="alert">{error}</div>}
            {people.length === 0 ? (
              <p className="hint">{t("transfer.peopleNone")}</p>
            ) : (
              <>
                <p className="hint">{t("transfer.peopleHelp")}</p>
                <ul className="plain-list">
                  {people.map((p) => (
                    <li key={p.userId} className="row between">
                      <span>
                        {p.name || p.email} · {p.email} · {t(`role.${p.role}`)}
                      </span>
                      <button className="btn ghost" aria-label={`${t("share.remove")} ${p.name || p.email}`} onClick={() => void remove(p.userId)}>
                        {t("share.remove")}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <a className="btn secondary" href={`#/agents/${agentId}/share`}>
              {t("transfer.manage")}
            </a>
          </>
        )}
      </main>
    );
  }
  if (invalid) return <main className="page" id="main"><div className="banner warn" role="alert">{t("transfer.invalid")}</div></main>;
  if (!offer) return <main className="page" id="main"><p>{t("common.loading")}</p></main>;

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.acceptTransfer(ref, account);
      const fresh = await api.me();
      setAgentId(r.agentId);
      setOverPlan(r.overPlan ?? null);
      setPeople(r.people ?? []);
      setOutcome(t("transfer.accepted", { agent: offer.agentName }));
      onDone(fresh, r.agentId);
    } catch (err) {
      setError(errorText(err, t("common.network")));
    } finally {
      setBusy(false);
    }
  };
  const decline = async () => {
    setBusy(true);
    try {
      await api.declineTransfer(ref);
      setOutcome(t("transfer.declined"));
    } catch (err) {
      setError(errorText(err, t("common.network")));
    } finally {
      setBusy(false);
    }
  };

  const mine = me.user.email.toLowerCase() === offer.toEmail.toLowerCase();
  return (
    <main className="page" id="main">
      <h1>{t("transfer.title")}</h1>
      <p>{t("transfer.body", { sender: offer.fromName || offer.fromAccountName, account: offer.fromAccountName, agent: offer.agentName })}</p>
      {error && <div className="banner warn" role="alert">{error}</div>}
      {!mine ? (
        <p>{t("transfer.signInFirst", { email: offer.toEmail })}</p>
      ) : !offer.accounts?.length ? (
        <p>{t("transfer.noAccounts")}</p>
      ) : (
        <div className="card">
          <label htmlFor="transfer-account">{t("transfer.choose")}</label>
          <select id="transfer-account" value={account} onChange={(e) => setAccount(e.target.value)}>
            {offer.accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <div className="row">
            <button className="btn primary" disabled={busy || !account} onClick={() => void accept()}>
              {t("transfer.accept")}
            </button>
            <button className="btn secondary" disabled={busy} onClick={() => void decline()}>
              {t("transfer.decline")}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
